import { defineStore } from 'pinia';
import {
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  getAuth,
  signInWithCustomToken,
  type User,
} from 'firebase/auth';
import { app, db } from 'boot/firebase';
import { getCallableFunction } from '@/utils/firebaseFunctions';
import { type AppUser, type UserProfile, type UserRole } from 'components/models';
import {
  doc,
  getDoc,
  onSnapshot,
  type DocumentReference,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { DATABASE_CONFIG, NOTIFICATION_CONFIG } from '../config/appConfig';

/**
 * Marker the `blockUnprovisionedAccounts` blocking function prefixes to its
 * rejection message, so a refused-because-unprovisioned sign-in can be told
 * apart from any other login failure.
 * MIRROR of UNPROVISIONED_ACCOUNT_MARKER in
 * functions/src/identity/blockUnprovisionedAccounts.ts — change both together.
 */
const UNPROVISIONED_ACCOUNT_MARKER = 'OOVI_UNPROVISIONED_ACCOUNT';

/**
 * Firebase auth error codes that mean the SESSION ITSELF is invalid — the only
 * conditions that justify forcing a sign-out during session bootstrap.
 *
 * WHY (2026-08-23): `processUser` previously ended in a blanket
 * `catch { signOut() }`, so ANY error — a network blip, Firestore `unavailable`,
 * a stale bundle served mid-deploy — became a hard logout. The router guard then
 * bounced every re-login attempt straight back to /login, turning a condition
 * that healed on its own into a self-sustaining lockout. Anything not listed here
 * is treated as transient: retry, then LEAVE THE SESSION ALONE.
 *
 * Note: a genuinely deleted / disabled account is still caught by the explicit
 * `!userSnap.exists() || isDeleted` check inside `processUser` — that path is
 * unchanged and still signs out.
 */
const TERMINAL_AUTH_ERROR_CODES: ReadonlySet<string> = new Set([
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/user-signed-out',
]);

/** Backoffs for transient session-bootstrap failures. Length = retry count. */
const AUTH_BOOTSTRAP_RETRY_BACKOFFS_MS = [400, 1200] as const;

/**
 * Own-profile listener (users/{uid}, single doc). Registered in
 * docs/FIRESTORE_LISTENERS.md. Module-scoped so logout / sign-out can detach it.
 */
let ownProfileUnsub: Unsubscribe | null = null;

/**
 * After users.name changes, the backend trigger rewrites the custom claim a
 * moment later. Force-refreshing the ID token too early would fetch a token that
 * still carries the old claim, so the refresh waits this long.
 */
const OWN_NAME_TOKEN_REFRESH_DELAY_MS = 5000;

/**
 * Drop every in-memory / localStorage copy of class data a previous user saw:
 * the instant cache (class-detail rosters), the teacher class store and the
 * class-list cache. Shared iPads and phones switch between teachers and 救生員,
 * whose payloads differ (救生員 get whitelisted fields). Lazy imports keep the
 * auth store free of import cycles.
 */
async function clearClassDataCaches(): Promise<void> {
  try {
    const { clearAllInstantCache } = await import('@/composables/useInstantData');
    clearAllInstantCache();
    const { useTeacherClassStore } = await import('@/stores/teacherClassStore');
    const teacherClassStore = useTeacherClassStore();
    teacherClassStore.setSelectedClass(null);
    teacherClassStore.setClasses([], '', []);
    const { useTeacherClassCache } = await import('@/composables/useTeacherClassCache');
    useTeacherClassCache().cache.invalidate();
  } catch (error) {
    // A failed chunk load must not hold sign-in / boot hostage.
    console.warn('[Auth] clearing cached class data failed:', error);
  }
}

/** A Firestore Timestamp as the SDK delivers it on a doc read (duck-typed on toMillis). */
function isFirestoreTimestamp(value: unknown): value is Timestamp {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toMillis?: unknown }).toMillis === 'function'
  );
}

/**
 * The users-doc `dismissedFlags` map (常態班更名 DT9 banner), keeping only Timestamp
 * values; `{}` when the field is absent or malformed.
 */
function readDismissedFlags(raw: unknown): Record<string, Timestamp> {
  const flags: Record<string, Timestamp> = {};
  if (typeof raw !== 'object' || raw === null) return flags;
  for (const [key, value] of Object.entries(raw)) {
    if (isFirestoreTimestamp(value)) flags[key] = value;
  }
  return flags;
}

/** Same keys at the same instants (millis) — an unchanged map must not churn `user`. */
function sameDismissedFlags(
  current: Record<string, Timestamp> | undefined,
  fresh: Record<string, Timestamp>,
): boolean {
  const keys = Object.keys(current ?? {});
  if (keys.length !== Object.keys(fresh).length) return false;
  return keys.every((key) => {
    const was = current?.[key];
    const now = fresh[key];
    return isFirestoreTimestamp(was) && !!now && was.toMillis() === now.toMillis();
  });
}

/** Narrow an unknown throwable to a Firebase error code, if it carries one. */
function firebaseErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

export interface AuthState {
  user: AppUser | null;
  loading: boolean;
  isReady: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    loading: false,
    isReady: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    userRoles: (state) => state.user?.roles ?? [],
    hasRole: (state) => (role: UserProfile['roles'][number]) =>
      state.user?.roles.includes(role) ?? false,
    // Backward-compat: returns highest-priority role. Used by teacher/admin pages
    // that pass viewAsRole to Cloud Functions for data filtering.
    viewAsRole: (state): string | null => {
      const roles = state.user?.roles;
      if (!roles || roles.length === 0) return null;
      const priority: UserRole[] = [
        'supervisor',
        'admin',
        'treasury',
        'general_affairs',
        'teacher',
        'kiosk',
      ];
      return (priority.find((r) => roles.includes(r)) ?? roles[0] ?? null) as UserRole | null;
    },
    // 🔥 CRITICAL: USERNAME DISPLAY PROTOCOL - NEVER REMOVE OR MODIFY THIS GETTER
    // This is the SINGLE SOURCE OF TRUTH for username display throughout the entire application
    // Prevents email fallback issues that have occurred multiple times in the past
    // ALL components must use authStore.displayName for username display
    // See docs/SYSTEM_ARCHITECTURE_GUIDE.md - "USERNAME DISPLAY PROTOCOL" section
    displayName: (state) => {
      if (!state.user) return null;
      // Always prioritize name field, fallback to email if name is missing/empty
      return state.user.name && state.user.name.trim() ? state.user.name : state.user.email;
    },
  },

  actions: {
    async forceTokenRefresh() {
      const auth = getAuth(app);
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log('[Auth] Forcing token refresh...');
        try {
          const idTokenResult = await currentUser.getIdTokenResult(true); // Force refresh
          const userRoles = Array.isArray(idTokenResult.claims.roles)
            ? idTokenResult.claims.roles
            : [];
          if (this.user) {
            // Detect top-priority role change before updating roles
            const rolePriority: UserRole[] = [
              'supervisor',
              'admin',
              'treasury',
              'general_affairs',
              'teacher',
              'kiosk',
            ];
            const oldTopRole =
              rolePriority.find((r) => this.user!.roles.includes(r)) ?? this.user!.roles[0] ?? null;
            const newTopRole =
              rolePriority.find((r) => (userRoles as UserRole[]).includes(r)) ??
              (userRoles as UserRole[])[0] ??
              null;

            this.user.roles = userRoles as UserProfile['roles'];
            console.log(`[Auth] Token refreshed. New roles: ${userRoles.join(', ')}`);

            if (oldTopRole !== newTopRole) {
              const { clearAllInstantCache } = await import('@/composables/useInstantData');
              clearAllInstantCache();
              if (import.meta.env.DEV) {
                console.log('[Auth] Top role changed — instant cache cleared.');
              }
            }
          }
        } catch (error) {
          console.error('[Auth] Failed to force token refresh:', error);
        }
      }
    },

    async loginWithEmail(email: string, pass: string) {
      this.loading = true;
      const auth = getAuth(app);
      try {
        await signInWithEmailAndPassword(auth, email, pass);
      } catch (error) {
        console.error('Email login error:', (error as Error).message);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async loginKioskWithPin(pin: string) {
      this.loading = true;
      try {
        const loginFunction = getCallableFunction('loginKioskWithPin');
        const result = await loginFunction({ pin });
        const { token } = result.data as { token: string };

        if (!token) {
          throw new Error('無法取得 Kiosk 登入權杖');
        }

        const auth = getAuth(app);
        await signInWithCustomToken(auth, token);
      } catch (error) {
        console.error('Kiosk PIN login error:', (error as Error).message);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async loginWithGoogle() {
      this.loading = true;
      const auth = getAuth(app);
      try {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        await signInWithRedirect(auth, provider);
      } catch (error) {
        console.error('Login error:', (error as Error).message);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reports why a Google redirect sign-in failed, if it did.
     *
     * `signInWithRedirect` navigates away, so a failure surfaces on the RETURN
     * leg — never from the call above. Without this the user is dropped back on
     * the login page with NO explanation at all. That matters most for an
     * account the backend has not provisioned: the `blockUnprovisionedAccounts`
     * blocking function refuses it, and silence would look like a broken button.
     *
     * Called from LoginPage's onMounted. Returns the notification payload to
     * show, or null when the redirect succeeded or none was pending.
     */
    async consumeRedirectError(): Promise<{ title: string; content: string } | null> {
      try {
        await getRedirectResult(getAuth(app));
        return null;
      } catch (error) {
        const message = (error as Error)?.message ?? '';
        const code = (error as { code?: string })?.code ?? '';
        console.error('[Auth] Google redirect sign-in failed:', code, message);

        // Identity Platform wraps blocking-function rejections in a generic
        // auth/internal-error whose text is server plumbing, so match on the
        // marker the CF prefixes rather than showing the raw message.
        // Marker defined in functions/src/identity/blockUnprovisionedAccounts.ts
        if (message.includes(UNPROVISIONED_ACCOUNT_MARKER)) {
          return NOTIFICATION_CONFIG.MESSAGES.LOGIN_UNPROVISIONED;
        }
        // A DISABLED account is a different situation from an unprovisioned one
        // and needs different words: the person has an account, a supervisor
        // switched it off. Falling through to the generic "try again" message
        // told a deactivated employee to keep retrying something that can never
        // succeed, and gave the supervisor no clue what had happened.
        if (code === 'auth/user-disabled' || message.includes('USER_DISABLED')) {
          return NOTIFICATION_CONFIG.MESSAGES.LOGIN_DISABLED;
        }
        return NOTIFICATION_CONFIG.MESSAGES.LOGIN_FAILED;
      }
    },

    /**
     * Keep the signed-in user's own name live. `users.name` is the in-app
     * identity (utils/personName.ts); it changes when a supervisor edits the
     * 暱稱, and before this the header only caught up on a full reload while
     * Cloud Functions kept stamping the old name from the token for up to an
     * hour. One single-doc listener per session.
     *
     * It also keeps `user.dismissedFlags` live (常態班更名 DT9 / A24 — the monthly BF
     * banner): a dismissal on another device hides the banner here too. The map is
     * assigned in place and only when a key or an instant changed, so `this.user`
     * keeps its identity (MainLayout's `watch(() => authStore.user)` re-runs the
     * idle prefetch on a new object).
     */
    watchOwnProfile(firebaseUser: User, userRef: DocumentReference) {
      this.stopWatchingOwnProfile();
      ownProfileUnsub = onSnapshot(
        userRef,
        (snap) => {
          if (!snap.exists()) return;
          const data = snap.data() as
            | { name?: unknown; isDeleted?: unknown; dismissedFlags?: unknown }
            | undefined;
          if (!data || data.isDeleted === true) return;
          // Before the name check below, which returns early when the name is unchanged.
          if (this.user && this.user.uid === firebaseUser.uid) {
            const freshFlags = readDismissedFlags(data.dismissedFlags);
            if (!sameDismissedFlags(this.user.dismissedFlags, freshFlags)) {
              this.user.dismissedFlags = freshFlags;
            }
          }
          const freshName = typeof data.name === 'string' ? data.name.trim() : '';
          if (!freshName || !this.user || this.user.uid !== firebaseUser.uid) return;
          if (freshName === this.user.name) return;
          console.log(`[Auth] own profile: name "${this.user.name}" → "${freshName}"`);
          this.user = { ...this.user, name: freshName, displayName: freshName };
          // Pull a token with the updated `name` claim so CF stamps switch too.
          setTimeout(() => {
            firebaseUser.getIdToken(true).catch((e: unknown) => {
              console.warn('[Auth] own profile: token refresh after rename failed', e);
            });
          }, OWN_NAME_TOKEN_REFRESH_DELAY_MS);
        },
        (error) => {
          console.warn('[Auth] own profile listener error (name may lag until reload):', error);
        },
      );
    },

    stopWatchingOwnProfile() {
      if (ownProfileUnsub) {
        ownProfileUnsub();
        ownProfileUnsub = null;
      }
    },

    /**
     * Local mirror of a successful sessionEnrollments-dismissUserBanner write, so every
     * mount in this session hides the banner at once. The CF stores the server time; the
     * own-profile listener replaces this value with it. Never written to Firestore here.
     */
    setOwnDismissedFlag(key: string, at: Timestamp) {
      if (!this.user) return;
      this.user.dismissedFlags = { ...(this.user.dismissedFlags ?? {}), [key]: at };
    },

    async logout() {
      this.loading = true;
      try {
        this.stopWatchingOwnProfile();
        // Clear cached class data before signOut to prevent cross-user data leakage
        await clearClassDataCaches();
        await signOut(getAuth(app));
        this.user = null;
        sessionStorage.removeItem('hasBeenRedirectedToMobile');
        // Clear payment store to prevent data leakage between sessions
        const { usePaymentStore } = await import('@/stores/paymentStore');
        usePaymentStore().resetAll();
        // Same for the 費用明細表's 消費日期 自動帶入 rows (itemId → date) of this session.
        const { useLineItemFollowStore } = await import('@/stores/lineItemFollowStore');
        useLineItemFollowStore().reset();
        // Clear pinned timeslot peek tabs — a SPA logout doesn't reload, so the
        // in-memory + sessionStorage pinned set would otherwise leak to the next user.
        const { resetTimeslotPeek } = await import('@/composables/useTimeslotPeek');
        resetTimeslotPeek();
        // Detach the stored-timeslot-directory listener and forget its state —
        // the SDK kills it on the post-sign-out permission error; a stale handle
        // would block every re-attach after the next login (2026-09-29).
        const { resetTimeslotDirectory } = await import('@/composables/useTimeSlotData');
        resetTimeslotDirectory();
        // Clear the per-employee 出勤 access probe — a SPA logout doesn't reload,
        // so the module-scoped attendanceEnabled would otherwise leak the previous
        // user's 出勤-module visibility to the next session in this browser tab.
        const { resetAttendanceAccess } = await import('@/composables/useAttendanceAccess');
        resetAttendanceAccess();
        // The 學生名冊 (names + phones) is a module singleton — drop it on sign-out
        // (deep review C15), not only when the next user signs in.
        const { resetStudentDirectory } = await import('@/composables/useStudentDirectory');
        resetStudentDirectory();
        // 家庭帳號 panel facts (課務備註 included): never served to the next login (W9 VR-03).
        const { clearFamilyFacts } = await import('@/composables/useFamilyFacts');
        clearFamilyFacts();
        console.log('[Auth] User logged out.');
      } catch (error) {
        console.error('Logout error:', (error as Error).message);
      } finally {
        this.loading = false;
      }
    },

    init() {
      if (this.isReady) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        const auth = getAuth(app);

        const processUser = async (firebaseUser: User | null, attempt = 0): Promise<void> => {
          if (!firebaseUser) {
            this.user = null;
            console.log('[Auth] processUser: No Firebase user, setting local user to null.');
            return;
          }

          try {
            // Fetch roles from token first — needed for both regular and kiosk users
            const idTokenResult = await firebaseUser.getIdTokenResult(true);
            const userRoles = (idTokenResult.claims.roles || []) as UserProfile['roles'];
            console.log(`[Auth] processUser: Fetched user roles from token: ${userRoles}`);

            // Kiosk custom token users have no email, no provider, and no users/ document.
            // They authenticate via PIN → signInWithCustomToken, which provides
            // a valid UID and roles in claims but no Firestore profile.
            // Discriminate by providerData (empty for custom tokens) rather than email,
            // so a Google user with an unusual account won't accidentally hit this path.
            const isCustomTokenUser = firebaseUser.providerData.length === 0;
            if (isCustomTokenUser) {
              console.log(
                '[Auth] processUser: No email (custom token user). Using token claims only.',
              );
              const newUser: AppUser = {
                ...firebaseUser,
                name: 'Kiosk',
                displayName: 'Kiosk',
                roles: userRoles,
              } as AppUser;
              this.user = newUser;
              return;
            }

            const userRef = doc(db, DATABASE_CONFIG.COLLECTIONS.USERS, firebaseUser.uid);
            const userSnap = await getDoc(userRef);

            console.log(
              `[Auth] processUser: Checking users collection for doc ID ${
                firebaseUser.uid
              }... exists: ${userSnap.exists()}`,
            );

            if (!userSnap.exists() || userSnap.data()?.isDeleted) {
              console.log(
                '[Auth] processUser: User document not found or soft-deleted. Signing out.',
              );
              await signOut(auth);
              this.user = null;
              return;
            }

            const userProfile = userSnap.data() as UserProfile;

            const newUser: AppUser = {
              ...firebaseUser,
              ...userProfile,
              name: userProfile.name || firebaseUser.displayName,
              displayName: userProfile.name || firebaseUser.displayName,
              roles: userRoles,
            };
            this.user = newUser;
            this.watchOwnProfile(firebaseUser, userRef);

            console.log(
              `[Auth] processUser: User ${newUser.uid} (name: ${newUser.displayName}, email: ${
                newUser.email
              }, roles: ${newUser.roles.join(', ')}) processed and set.`,
            );
          } catch (e) {
            const code = firebaseErrorCode(e);

            // Session genuinely invalid — sign out (previous behaviour, now scoped).
            if (code && TERMINAL_AUTH_ERROR_CODES.has(code)) {
              console.error('[Auth] processUser: terminal auth error — signing out:', code, e);
              this.user = null;
              await signOut(auth);
              return;
            }

            // Transient — retry before giving up.
            const backoffMs = AUTH_BOOTSTRAP_RETRY_BACKOFFS_MS[attempt];
            if (backoffMs !== undefined) {
              console.warn(
                `[Auth] processUser: transient error (${code ?? 'unknown'}); retry ${
                  attempt + 1
                }/${AUTH_BOOTSTRAP_RETRY_BACKOFFS_MS.length} in ${backoffMs}ms`,
                e,
              );
              await new Promise((resolve) => setTimeout(resolve, backoffMs));
              return processUser(firebaseUser, attempt + 1);
            }

            // Retries exhausted. KEEP the Firebase session and leave `this.user`
            // untouched: onIdTokenChanged / the next reload can still recover.
            // Signing out here is what caused the 2026-08-23 lockout loop.
            console.error(
              '[Auth] processUser: transient error persisted — keeping session, NOT signing out:',
              e,
            );
          }
        };

        onAuthStateChanged(auth, async (firebaseUser) => {
          console.log(`[Auth] onAuthStateChanged triggered. User: ${firebaseUser?.uid || 'null'}`);
          await processUser(firebaseUser);
          if (!firebaseUser) this.stopWatchingOwnProfile();

          // Safety net for token-expiry / external sign-out (paths that bypass the
          // logout() action): when the user is gone, drop any pinned peek tabs, the
          // 出勤 access probe and the 學生名冊 so they never carry into a different
          // session in the same browser tab.
          if (!firebaseUser) {
            // Also covers the forced sign-outs in processUser (deleted user doc,
            // terminal auth error): signOut() lands here with no user.
            await clearClassDataCaches();
            const { resetTimeslotPeek } = await import('@/composables/useTimeslotPeek');
            resetTimeslotPeek();
            const { resetTimeslotDirectory } = await import('@/composables/useTimeSlotData');
            resetTimeslotDirectory();
            const { resetAttendanceAccess } = await import('@/composables/useAttendanceAccess');
            resetAttendanceAccess();
            const { resetStudentDirectory } = await import('@/composables/useStudentDirectory');
            resetStudentDirectory();
          }

          if (!this.isReady) {
            this.isReady = true;
            this.loading = false;
            console.log('[Auth] Initialization complete.');
            resolve();
          }
        });

        // onAuthStateChanged only fires on sign-in / sign-out, so a role change made
        // server-side (custom claims) would not reach an already-open session until a
        // full re-login. The ID token silently refreshes (~hourly, or on force-refresh),
        // and onIdTokenChanged fires with the updated claims — re-sync roles here so the
        // client view matches the server's permissions without requiring a re-login.
        onIdTokenChanged(auth, async (firebaseUser) => {
          // Sign-in / sign-out are handled by onAuthStateChanged above. Only act when an
          // existing session's token refreshes with different roles than we currently hold.
          if (!firebaseUser || !this.user || this.user.uid !== firebaseUser.uid) return;
          try {
            // No force-refresh — read the already-refreshed token's claims to avoid a
            // refresh→listener loop. processUser's own force-refresh is broken by the
            // role-change guard below (roles will match on the second pass).
            const idTokenResult = await firebaseUser.getIdTokenResult();
            const freshRoles = (idTokenResult.claims.roles || []) as UserProfile['roles'];
            const currentRoles = this.user.roles ?? [];
            const rolesChanged =
              freshRoles.length !== currentRoles.length ||
              freshRoles.some((r) => !currentRoles.includes(r));
            if (rolesChanged) {
              console.log(
                `[Auth] onIdTokenChanged: roles changed [${currentRoles.join(', ')}] → [${freshRoles.join(
                  ', ',
                )}]. Re-syncing session.`,
              );
              await processUser(firebaseUser);
            }
          } catch (e) {
            console.error('[Auth] onIdTokenChanged role-sync error:', e);
          }
        });
      });
    },
  },
});
